import { FC, ReactNode } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import type { TaskType } from "@myapp/contract";
import { FieldWrapper, FormInput, RecurrenceField } from "../../../shared/ui";
import { AttachmentsSection } from "../../../shared/ui/controls/AttachmentsSection/AttachmentsSection";
import { SegmentedControl } from "../../../shared/ui/controls/SegmentedControl";
import { FormSelect } from "../../../shared/ui/controls/FormSelect";
import { HandwerkerSearchSelect } from "../../../entities/user";
import { taskStatusOptions } from "../../../entities/task";
import { taskHasActuals, type TaskFormValues } from "../model/taskForm.schema";

/**
 * Fields only — no queries, no mutations. Everything arrives as props from `UpsertTaskModal`,
 * which owns the data and the submit.
 *
 * **One shape for both task types.** The type is edited here like any other field and decides
 * nothing about the layout; it is a category for the list filter and the Arbeitszeit-Report. See
 * the rule block in `../model/taskForm.schema.ts`.
 *
 * WHAT GOES WHERE, and why it is not arbitrary: *Aufgabe* holds what every task has — Handwerker
 * (the only required field besides the title) and Abgabefrist. *Termin* holds only the
 * reservation, which is optional. The Handwerker cannot live under the Termin heading, because it
 * is required even when there is no Termin at all.
 *
 * ASTERISKS mark what is unconditionally required, so the termin fields carry none. The durations
 * *are* required — but only once a start is set, and a conditional rule is better told by the
 * inline error than by a mark that is wrong half the time.
 *
 * `FormSection` is deliberately not used: its white card inside a white modal was a container with
 * nothing to contain. Grouping is proximity plus a hairline instead.
 */

const TASK_TYPE_ITEMS: { value: TaskType; label: string }[] = [
  { value: "GENERAL_TASK", label: "Allgemeine Aufgabe" },
  { value: "ERRANDS", label: "Besorgung" },
];

const SERIES_SCOPE_ITEMS = [
  { value: "single" as const, label: "Nur diese Aufgabe" },
  { value: "series" as const, label: "Diese und alle folgenden" },
];

const Section: FC<{
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}> = ({ title, hint, action, children }) => (
  <section className="flex flex-col gap-4 border-t border-slate-200 pt-6 first:border-0 first:pt-0">
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="font-['Roboto'] text-[15px] font-bold leading-none text-[#124093]">
          {title}
        </h3>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

interface Props {
  control: Control<TaskFormValues>;
  mode: "create" | "edit";
  /** Edit mode: the task belongs to a series, so the scope toggle applies. */
  hasSeries: boolean;
  /** Current `plannedStart` — the anchor the recurrence preview is derived from. */
  plannedStart: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingAttachments: string[];
  onRemoveExistingAttachment: (index: number) => void;
}

export const TaskFormFields: FC<Props> = ({
  control,
  mode,
  hasSeries,
  plannedStart,
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
}) => {
  /**
   * Read from the form rather than from the loaded task: the status is editable, so the Ist-Zeiten
   * have to appear in the same render as the user picking "Abgeschlossen". The mapper gates on the
   * same value — see `taskHasActuals`. Mirrors `AppointmentFormFields`.
   */
  const status = useWatch({ control, name: "status" });
  const showActuals = taskHasActuals(status);

  return (
    <div className="flex w-full flex-col gap-6">
      <Section title="Aufgabe">
        <Controller
          name="taskType"
          control={control}
          render={({ field }) => (
            <SegmentedControl
              size="sm"
              className="self-start"
              items={TASK_TYPE_ITEMS}
              value={field.value}
              onChange={field.onChange}
              ariaLabel="Aufgabentyp"
            />
          )}
        />

        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          <FieldWrapper label="Titel" required className="col-span-2 sm:col-span-6">
            <FormInput name="title" control={control} as="input" />
          </FieldWrapper>

          {/*
            Beide gelten für jede Aufgabe: den Handwerker braucht sie immer, die Abgabefrist ist
            frei wählbar und unabhängig vom Termin — eine Aufgabe darf von beidem begrenzt sein.
          */}
          <FieldWrapper label="Handwerker" required className="col-span-2 sm:col-span-3">
            <Controller
              name="assignedTechnicianId"
              control={control}
              render={({ field, fieldState }) => (
                <HandwerkerSearchSelect
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  error={fieldState.error?.message ?? null}
                  inputRef={field.ref}
                  placeholder="Handwerker suchen..."
                />
              )}
            />
          </FieldWrapper>

          <FieldWrapper label="Abgabefrist" className="col-span-2 sm:col-span-3">
            <FormInput name="deadline" control={control} type="date" as="input" />
          </FieldWrapper>

          {mode === "edit" && (
            <FieldWrapper label="Status" className="col-span-2 sm:col-span-3">
              <FormSelect name="status" control={control} options={taskStatusOptions} />
            </FieldWrapper>
          )}
        </div>
      </Section>

      <Section
        title="Termin"
        hint="Ohne Termin bleibt die Aufgabe ungeplant und kann später im Kalender geplant werden."
      >
        {/*
          One 6-column grid for the whole section rather than a 2-col row stacked on a 3-col
          row: 6 divides by both, so the halves and the thirds share vertical edges.
        */}
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          <FieldWrapper label="Termin um" className="col-span-2 sm:col-span-3">
            <FormInput name="plannedStart" control={control} type="datetime-local" as="input" />
          </FieldWrapper>

          {/* col-start-1 keeps the durations on a row of their own whatever precedes them. */}
          <FieldWrapper label="Anfahrt (min)" className="sm:col-span-2 sm:col-start-1">
            <FormInput
              name="plannedTravelDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Handwerker Arbeitszeit (min)" className="sm:col-span-2">
            <FormInput
              name="plannedWorkDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Abfahrt (min)" className="sm:col-span-2">
            <FormInput
              name="plannedDepartureDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>

          {/*
            Ist-Zeiten, only for a completed Aufgabe. Same columns directly under their planned
            counterparts so a correction is read as a comparison, not as a new set of fields.
            `taskHasActuals` also gates the submit mapper — the two must not drift apart.

            These four are what the calendar draws once the Aufgabe is completed: occupancy is
            `COALESCE(actual, planned)`, so a drag on the board retypes *these* inputs and not the
            planned ones above. Which is why the Ist start is an ordinary visible field rather than
            something only a drag can reach.
          */}
          {showActuals && (
            <>
              <FieldWrapper
                label="Ist-Termin um"
                className="col-span-2 sm:col-span-3 sm:col-start-1"
              >
                <FormInput
                  name="actualStart"
                  control={control}
                  type="datetime-local"
                  as="input"
                />
              </FieldWrapper>
              <FieldWrapper label="Ist-Anfahrt (min)" className="sm:col-span-2 sm:col-start-1">
                <FormInput
                  name="actualTravelDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
              <FieldWrapper label="Ist-Arbeitszeit (min)" className="sm:col-span-2">
                <FormInput
                  name="actualWorkDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
              <FieldWrapper label="Ist-Abfahrt (min)" className="sm:col-span-2">
                <FormInput
                  name="actualDepartureDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
            </>
          )}
        </div>

        {/*
          Create offers the rule; edit offers only the scope, because the frequency of an
          existing series is not patchable.

          `RecurrenceField` disables itself while `startDate` is empty — a series needs an anchor,
          and now that the Termin is optional that is a state the form can actually be in.
        */}
        {mode === "create" ? (
          <Controller
            name="recurrence"
            control={control}
            render={({ field }) => (
              <RecurrenceField
                startDate={plannedStart}
                value={field.value}
                onChange={field.onChange}
                label="Als Serie wiederholen"
              />
            )}
          />
        ) : hasSeries ? (
          <Controller
            name="forFullSeries"
            control={control}
            render={({ field }) => (
              <SegmentedControl
                size="sm"
                className="self-start"
                items={SERIES_SCOPE_ITEMS}
                value={field.value ? "series" : "single"}
                onChange={(next) => field.onChange(next === "series")}
                ariaLabel="Änderungsumfang"
              />
            )}
          />
        ) : null}
      </Section>

      <Section title="Details">
        <FieldWrapper label="Beschreibung">
          <FormInput name="description" control={control} as="textarea" rows={3} />
        </FieldWrapper>

        <AttachmentsSection
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={onRemoveExistingAttachment}
          files={files}
          onFilesChange={onFilesChange}
        />
      </Section>
    </div>
  );
};
